import axios from "axios";
import { format, parseISO } from "date-fns";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";


import { useLocalStorage } from "../../../hooks";
import * as dashboardActions from "../../../redux/reducers/dashboardReducer";
import { dashboardState } from "../../../redux/reducers/dashboardReducer";

import { FunnelIcon } from "@heroicons/react/20/solid";
import moment from "moment";
import profile_img from "../../../assets/avatar_placeholder.png";
import defaultImage from "../../../assets/defaultImage.svg";
import greenlocation from "../../../assets/greenlocation.svg";
import greentime from "../../../assets/greentime.svg";
import { TwButton, TwInputGroup, TwListBox, TwPopover, TwRating } from "../../../components/tailwind";
import { cutString } from "../../../helpers";
import { userState } from "../../../redux/reducers/userReducer";
import { getUrlForBookings } from "../../../utils/confighelpers";
import { bookingStatusFilter, displayAvailablePaymentMethods } from "../../../utils/constants";
import SessionBookingPopover from "./SessionBookingPopover";
import { AdjustmentsHorizontalIcon, ArrowPathIcon, ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import dropdownbottom from "../../../assets/dropdownbottom.svg";

export default function SessionBooking({
  sessionDataToDisplay,
  displayHeader,
  metricsFilterList = [],
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [userToken] = useLocalStorage("userToken");
  const dashboardReduxState = useSelector(dashboardState);
  const userReduxState = useSelector(userState);
  const [listLoading, setListLoading] = React.useState(false);
  const [bookingFilter, setBookingFilter] = React.useState('all');
  const [recentBookingsList, setRecentBookingsList] = React.useState([]);
  const [showViewMore, setShowViewMore] = React.useState(false);
  const [recentBookingsPageNumber, setRecentBookingsPageNumber] = React.useState(1);
  const [metricsFilter, setMetricsFilter] = React.useState(metricsFilterList?.[0]?.value || 'all');
  const [metricsData, setMetricsData] = React.useState({});
  const [orders, setOrders] = React.useState({});
  const [searchText, setSearchText] = React.useState("");

  const columns = [
    { key: "bookingId", label: "Order ID" },
    { key: "bookingDate", label: "Date" },
    { key: "consumer_detail.fullName", label: "Buyer Name" },
    { key: "consumer_detail.email", label: "Email" },
    { key: "amount", label: "Amount" },
    { key: "currency", label: "Currency" },
    { key: "paymentStatus", label: "Status" },
    { key: "sellerReferenceId", label: "Seller Name" },
  ];

  // ---------- EXPORT TO EXCEL ----------
  const handleExportExcel = () => {
    const data = recentBookingsList.map((order) => {

      // Collect dietary preferences from individual tickets' included addons
      const dietaryList = [];
      (order?.tickets || []).forEach((ticket, tIdx) => {
        (ticket?.includedAddons || []).forEach((addon) => {
          if (addon?.dietaryPreference) {
            dietaryList.push(`${ticket?.ticketType || ""} #${tIdx + 1} - ${addon?.addonName || ""}: ${addon.dietaryPreference}`);
          }
        });
      });

      const row = {
        "Order ID": order.bookingId,
        "Order Date": moment(order.bookingDate).format("MMM, D YYYY"),
        "Order Status": order.status,
        "Buyer Name": order?.consumer_detail?.fullName,
        "Phone Number": order.consumer_detail?.countryCode ? order.consumer_detail.countryCode + "_" + order.consumer_detail.phoneNumber : "" + order?.consumer_detail?.username || "",
        "Email": order?.consumer_detail?.email || "",
        "Event Name": order?.eventName || order?.event_schedule_detail?.eventName || "",
        "Event Date": order.event_schedule_detail && order.event_schedule_detail.eventDate ? moment(order.event_schedule_detail.eventDate).format("MMM, D YYYY") : "",
        "Tickets bought": `Total - ${calculateBoughtTickets(order)} (${order?.ticketsBought ? order.ticketsBought.map((ticket, i) => {
          return ticket.bookingQty + " x " + ticket.ticketType + " "
        }).join(" | ") : ""})`,
        "Dietary Preferences": dietaryList.length > 0 ? dietaryList.join("; ") : "",
        "Amount": `€${ticketAmount(order)}`,
        "Payment Status": (order.paymentStatus.toLowerCase() === "paid") ? "Paid Online" : order.paymentStatus,
        "Payment Method": getPaymentMethod(order),
        "Seller Name": order.sellerId && order.sellerId !== undefined && order.sellerId !== "" && metricsFilterList.length > 0 && metricsFilterList.find((f) => f.value == order.sellerId) !== undefined ? metricsFilterList.find((f) => f.value == order.sellerId).title : "Direct Sales",

      };
      return row;
    });

    // 2. Create worksheet & workbook
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

    // 3. Generate buffer
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    // 4. Save as file
    const blob = new Blob([excelBuffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(blob, `orders_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ---------- EXPORT TO PDF ----------
  const handleExportPDF = () => {
    const doc = new jsPDF();

    // Table headers (visible columns)
    const head = [columns.map((col) => col.label)];

    // Table body
    const body = recentBookingsList.map((order) => {
      return [
        order.bookingId,
        moment(order.bookingDate).format("MMM, D YYYY"),
        order?.consumer_detail?.fullName,
        order?.consumer_detail?.email || "",
        ticketAmount(order),
        "EUR",
        (order.paymentStatus.toLowerCase() === "paid") ? "Paid Online" : order.paymentStatus,
        order.sellerId && order.sellerId !== undefined && order.sellerId !== "" && metricsFilterList.length > 0 && metricsFilterList.find((f) => f.value == order.sellerId) !== undefined ? metricsFilterList.find((f) => f.value == order.sellerId).title : "Direct Sales",
      ]
    }
    );

    // Title
    doc.setFontSize(14);
    doc.text("Orders Export", 14, 16);

    // AutoTable
    doc.autoTable({
      head,
      body,
      startY: 22,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [240, 240, 240] },
    });

    doc.save(`orders_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  /* React.useEffect(() => {
    getRecentBookings();
  }, []); */
  React.useEffect(() => {
    if (sessionDataToDisplay._id) {
      getRecentBookings(recentBookingsPageNumber);
    }
  }, [recentBookingsPageNumber, bookingFilter, sessionDataToDisplay._id, dashboardReduxState.updateSessionComp, metricsFilter, searchText]);

  const getRecentBookings = async (page = 1) => {
    setShowViewMore(false);
    setListLoading(true);
    const manageClassesConfig = {
      method: "get",
      url: `${getUrlForBookings()}getMybookings?eventScheduleId=${sessionDataToDisplay._id}&page=${page}&filterBy=${bookingFilter}&listFor=${userReduxState.businessType}${metricsFilter ? `&sellerId=${metricsFilter}` : ''}${searchText && searchText !== "" ? `&searchText=${searchText}` : ''}`,
      headers: {
        "x-access-token": userToken,
      },
    };
    const manageClassesRequest = await axios(manageClassesConfig);
    if (manageClassesRequest.status === 200) {
      const rows = manageClassesRequest.data?.data?.data || [];
      if (recentBookingsPageNumber > 1) {
        if (rows.length) setRecentBookingsList((prev) => [...prev, ...rows]);
      } else {
        // Page 1 always REPLACES — so an empty/filtered result clears stale rows
        // instead of leaving the previous (unfiltered) list on screen.
        setRecentBookingsList(rows);
      }
      setShowViewMore(rows.length > 0);
    }
    setListLoading(false);
  };

  const ticketAmount = (row) => {
    let ticketAmount = 0;
    let serviceFees = 0;
    if (row?.totalPriceToConsumer) {
      return parseFloat(row?.totalPriceToConsumer || 0).toFixed(2)
    } else {
      if (row.ticketsBought && row.ticketsBought.length > 0) {
        row.ticketsBought.map((data) => {
          serviceFees = serviceFees + (parseFloat(data?.serviceFee ? data?.serviceFee : 0) * parseFloat(data?.bookingQty))
          ticketAmount = (parseFloat(ticketAmount) + parseFloat(data.priceToConsumer))
        })
        // ticketAmount = parseFloat(ticketAmount + parseFloat(serviceFees))
        ticketAmount = parseFloat(ticketAmount).toFixed(2)
      } else {
        ticketAmount = (row.amount ? row.amount : 0);
      }
      return parseFloat(ticketAmount).toFixed(2);
    }
  }

  const calculateBoughtTickets = (row) => {
    let totalBookedTickets = 0;

    if (row && row?.ticketsBought?.length) {
      row?.ticketsBought.forEach((item) => {
        totalBookedTickets = totalBookedTickets + parseInt(item.bookingQty)
      })
    }

    return totalBookedTickets
  }

  const getPaymentMethod = (row) => {
    if (row?.paymentMethod && row?.paymentMethod !== 'NA') {
      return Object.keys(displayAvailablePaymentMethods).indexOf(row?.paymentMethod) >= 0 ? displayAvailablePaymentMethods[row?.paymentMethod] : t("bookings.unknown");
    } /* else if (bookingInfo?.paymentStatus == "Unpaid" && bookingInfo.paymentMethod == "NA" && bookingInfo.paymentMode != "Online" && parseInt(bookingInfo.amount) > 0) {
              return 'pay at venue';
          } else if ((bookingInfo?.paymentStatus == "Paid" || bookingInfo?.paymentStatus == "Free") && bookingInfo.paymentMethod == "NA" && parseInt(bookingInfo.amount) <= 0) {
              //return 'Free';
              return 'Not Applicable';
          } */
    return t("bookings.unknown");
  };

  const handleFilterChange = (event) => {
    const { target: { value } } = event;
    setRecentBookingsPageNumber(1)
    setBookingFilter(value);
    setRecentBookingsList([])
  };

  return (
    <div className={""}>
      {displayHeader ?
        (<dev className="flex flex-col gap-3 w-full">
          <dev className="flex flex-col sm:flex-row gap-5">
            <div className="flex flex-auto flex-col gap-2">
              <div className={"w-full h-44 md:h-72 flex justify-center"}>
                {sessionDataToDisplay?.eventId?.eventPhoto ? (
                  <img
                    className={
                      "sm:h-60 lg:h-72 rounded-2xl"
                    }
                    src={sessionDataToDisplay?.eventId?.eventPhoto}
                    alt={""}
                  />
                ) : (<img
                  className={
                    "w-full sm:h-60 lg:h-72 object-cover rounded-2xl"
                  }
                  src={defaultImage}
                  alt={""}
                />)}
              </div>
              <div className={"grow"}>
                <h2 className={"font-bold text-xl md:text-3xl text-primary"}>
                  {sessionDataToDisplay?.eventName}
                </h2>
                <p className={"text-sm md:text-base"}>
                  {sessionDataToDisplay?.eventId?.tagLines}
                </p>
                <div
                  className={
                    "min-w-40 flex gap-2 items-left bg-white rounded-lg"
                  }
                >
                  <TwRating
                    rating={sessionDataToDisplay?.eventId?.rating}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-auto flex-col gap-6 justify-center items-left ">
              <div className="flex justify-center items-center md:w-4/5">
                <div className="flex-none">
                  <img
                    className={""}
                    src={greenlocation}
                    alt={""}
                  />
                </div>
                <div className="flex-1 pl-2">
                  <div className="text-gray-500 text-sm">{t("myEvents.where")} </div>
                  <div className="text-black text-base">
                    <p className="font-bold">{sessionDataToDisplay && sessionDataToDisplay.location ? sessionDataToDisplay.location.addressName : ""}</p>
                    <p>{sessionDataToDisplay && sessionDataToDisplay.location ? sessionDataToDisplay.location.address : ""}</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-center items-center md:w-4/5">
                <div className="flex-none">
                  <img
                    className={""}
                    src={greentime}
                    alt={""}
                  />
                </div>
                <div className="flex-1 pl-2">
                  <div className="text-gray-500 text-sm">{t("myEvents.eventDate")} </div>
                  <div className="text-black text-base">
                    {sessionDataToDisplay.eventDate ? format(parseISO(sessionDataToDisplay.eventDate), "EEEE, MMMM d 'at' hh:mm aa") + " - " + format(parseISO(sessionDataToDisplay.eventEndDate), "hh:mm aa") : ""}
                  </div>
                </div>
              </div>
            </div>
          </dev>
        </dev>) : ""}
      <div
        className={`max-w-7xl min-h-[500px] bg-white rounded-lg border-2 border-bright-gray hover:border-primary ${displayHeader ? "mt-16" : "mt-0"}`}
      >
        <div className="flex flex-col md:flex-row justify-between border-b-2 border-bright-gray p-5">
          <h3 className={"text-black text-2xl sm:text-3xl"} >
            {/* {t("bookings.bookingTitle")} */}
            {t("myEvents.orders")}
          </h3>
          <div className="flex flex-col md:flex-row gap-2 justify-end items-end md:items-center">
            <div className="flex gap-2 justify-end items-center">
              <TwInputGroup
                // label={""}
                placeholder={"Search"}
                className={"md:!w-24 !py-3 md:!pl-3"}
                leftIcon={<MagnifyingGlassIcon className="size-5" />}
                name={"searchText"}
                value={searchText}
                onChange={(event) => {
                  setRecentBookingsList([]);
                  const { name, value } = event.target;
                  setSearchText(value);
                }}
              />
              <TwButton variant={"default"} onClick={() => {
                if (recentBookingsPageNumber == 1) {
                  getRecentBookings(recentBookingsPageNumber)
                } else {
                  setRecentBookingsPageNumber(1);
                }
              }}>
                <ArrowPathIcon aria-hidden="true" className="size-5 text-black forced-colors:text-[Highlight]" />
              </TwButton>
            </div>
            <TwPopover
              buttonClassName={"[bg-info text-white] text-black rounded-lg w-full [sm:w-32] border border-gray-300"}
              buttonContent={(<span className={"flex flex-row items-center p-2 gap-2"}>
                <p className={"w-full"}>{t("myEvents.export")}</p>
                <ChevronDownIcon aria-hidden="true" className="size-5 text-black forced-colors:text-[Highlight]" />
              </span>)}
            >
              <div className={"flex flex-col gap-3 py-5"}>
                <button
                  onClick={handleExportExcel}
                  className={`hover:bg-gray-200 p-4 text-sm text-left text-black disabled:text-lilac`}
                >
                  {t("myEvents.exportToExcel")}
                </button>
                {/* <button
                  onClick={handleExportPDF}
                  className={`hover:bg-gray-200 p-4 text-sm text-left text-black disabled:text-lilac`}
                >
                  {t("myEvents.exportToPDF")}
                </button> */}
              </div>
            </TwPopover>

            <div className="flex gap-2 justify-end items-center">
              <TwListBox
                btnClass={"border"}
                value={metricsFilter}
                options={metricsFilterList}
                dropdownArrow={(<AdjustmentsHorizontalIcon aria-hidden="true" className="size-5 text-black forced-colors:text-[Highlight]" />)}
                onChange={(v) => {
                  // Reset paging + clear the list so the new seller filter REPLACES
                  // the rows (otherwise a page>1 fetch appends onto the full list,
                  // making the filter look like it shows everything).
                  setRecentBookingsPageNumber(1)
                  setRecentBookingsList([])
                  setMetricsFilter(v.value)
                  setMetricsData(v.data)
                }}
              />
              <TwListBox
                btnClass={"border"}
                value={bookingFilter}
                options={bookingStatusFilter}
                dropdownArrow={(<FunnelIcon aria-hidden="true" className="size-5 text-black forced-colors:text-[Highlight]" />)}
                onChange={(v) => {
                  handleFilterChange({ target: { value: v.value } })
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 p-5">
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full py-2 align-middle sm:px-1 overflow-x-auto">
              <div className="overflow-hidden [shadow] ring-0 ring-black/5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-form">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        {t("dashboard.customer")}
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        {t("bookings.bookingId")}
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        {t("dashboard.activityBooked")}
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        {t("bookings.ticketsBought")}
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        {t("bookings.amountPaid")}
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        {/* {t("bookings.amountPaid")} */}
                        Seller
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">{t("account.tableActionHead")}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {listLoading ? (
                      <tr>
                        <td colSpan={6} className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6 text-center">
                          {t("inputFieldsTitle.loading")}
                        </td>
                      </tr>
                    ) : (recentBookingsList.length > 0 ? recentBookingsList.map((row, i) => (
                      <tr key={i}>
                        <td className="whitespace-nowrap py-5 pt-6 pl-4 pr-3 text-sm font-bold text-gray-900 sm:pl-6 flex gap-2 items-center flex ">
                          <div className="flex justify-start items-center [min-w-4/5] w-full line-clamp-1">
                            <div className="flex-none">
                              {row?.consumer_detail?.profilePic ? (<img
                                className="size-8 rounded-full mr-3"
                                src={row.consumer_detail.profilePic}
                                alt={``}
                              />) : (<img
                                className="size-8 rounded-full mr-3"
                                src={profile_img}
                                alt={``}
                              />)}
                            </div>
                            <div className="flex-1 min-w-0 line-clamp-1">
                              {/* <div>{cutString(row?.studentName, 10, true)}</div> */}
                              <div className=" line-clamp-1">{row?.consumer_detail?.fullName}</div>
                              <div className="text-primary line-clamp-1">{(row.consumer_detail?.countryCode ? row.consumer_detail.countryCode + "_" + row.consumer_detail.phoneNumber : "" + row?.consumer_detail?.username)}</div>
                              <div className="[text-primary] line-clamp-1">{(row.consumer_detail?.email ? row.consumer_detail.email : "")}</div>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-bold"><div>{row.bookingId}<p className="text-xs text-gray-400 font-normal text-wrap"><span className={`${(row.status === "Cancelled") ? "text-bz-status-red" : "text-bz-status-green"}`}>{row.status}</span> | {moment(row.bookingDate).format("MMM D")}</p></div></td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-bold"><div>{row?.eventName || row?.event_detail?.eventName || row?.event_schedule_detail?.eventName || ""}<p className="text-xs text-gray-400 font-normal text-wrap">{row.event_schedule_detail && row.event_schedule_detail.eventDate ? moment(row.event_schedule_detail.eventDate).format('MMM D, hh:mm A') : ""}</p></div></td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-bold"><div>
                          {/* {row.bookingQty ? row.bookingQty : "1"} */}
                          {calculateBoughtTickets(row)}
                          <p className="text-xs text-gray-400 font-normal text-wrap" title={row?.ticketsBought ? row.ticketsBought.map((ticket, i) => {
                            return ticket.bookingQty + " x " + ticket.ticketType + " "
                          }).join(" | ") : ""}>
                            {cutString(row?.ticketsBought ? row.ticketsBought.map((ticket, i) => {
                              return ticket.bookingQty + " x " + ticket.ticketType + " " + (ticket?.isComplementaryTicket && ticket?.isComplementaryTicket == true ? t("bookings.complementaryTicket") : "")
                            }).join(" | ") : "", 25, true)}
                          </p>
                        </div></td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-bold">
                          <div>
                            €{ticketAmount(row)}
                            <p className="text-xs text-gray-400 font-normal text-wrap">{(row.paymentStatus.toLowerCase() === "paid") ? "Paid Online" : row.paymentStatus}</p>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-bold">
                          <div>
                            {(() => {
                              if (!row.sellerId || row.sellerId === "") return "Direct Sales";
                              const exact = metricsFilterList.find((f) => String(f.value) === String(row.sellerId) && f.data && f.data.offerId && row.offerId && String(f.data.offerId) === String(row.offerId));
                              const match = exact || metricsFilterList.find((f) => String(f.value) === String(row.sellerId));
                              if (!match) return "Direct Sales";
                              const d = match.data || {};
                              return (
                                <>
                                  <span>{d.name || match.title}</span>
                                  {d.offerLabel ? <p className="text-xs text-gray-400 font-normal">{d.offerLabel}</p> : null}
                                </>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <SessionBookingPopover
                            bookingDetails={row}
                            handleUpdate={() => {
                              getRecentBookings(recentBookingsPageNumber)
                              return dispatch(
                                dashboardActions.setUpdateSessionComp(
                                  dashboardReduxState.updateSessionComp + 1
                                )
                              )
                            }}
                          />
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6 text-center">
                          {t("bookings.noBookingsYet")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {false && (<div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="">
                <tr className="text-center">
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-center text-base font-extrabold text-gray-900 sm:pl-6 truncate">
                    {t("dashboard.customer")}
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-base font-extrabold text-gray-900 truncate">
                    {t("bookings.bookingId")}
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-base font-extrabold text-gray-900 truncate">
                    {t("dashboard.activityBooked")}
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-base font-extrabold text-gray-900 truncate">
                    SEATS BOOKED
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-base font-extrabold text-gray-900 truncate">
                    {t("bookings.amountPaid")}
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-center text-base font-extrabold text-gray-900"></th>
                </tr>
              </thead>
              <tbody className="divide-y-8 !border-y-0 divide-white ">
                {listLoading ? (
                  <tr>
                    <td colSpan={6} className="bg-td-row hover:bg-td-row-h whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6 text-center rounded-2xl">
                      {t("inputFieldsTitle.loading")}
                    </td>
                  </tr>
                ) : recentBookingsList.length ? recentBookingsList.map((row, i) => (
                  <tr key={i} className="bz-td-row">
                    <td className="bg-white whitespace-nowrap px-3 py-4 text-left text-sm font-bold rounded-l-2xl">
                      <div className="flex justify-center items-center min-w-4/5">
                        <div className="flex-none">
                          {row?.consumer_detail?.profilePic ? (<img
                            className="size-8 rounded-full mr-3"
                            src={row.consumer_detail.profilePic}
                            alt={``}
                          />) : (<img
                            className="size-8 rounded-full mr-3"
                            src={profile_img}
                            alt={``}
                          />)}
                        </div>
                        <div className="w-28">
                          {/* <div>{cutString(row?.studentName, 10, true)}</div> */}
                          <div>{cutString(row?.consumer_detail?.fullName, 10, true)}</div>
                          <div className="text-indigo-bz">{cutString((row.consumer_detail?.countryCode ? row.consumer_detail.countryCode + "_" + row.consumer_detail.phoneNumber : "" + row?.consumer_detail?.username), 10, true)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="bg-white whitespace-nowrap px-3 py-4 text-left text-sm font-bold">
                      {row.bookingId}
                      <p className="text-xs text-gray-400 font-normal text-wrap"><span className={`${(row.status === "Cancelled") ? "text-bz-status-red" : "text-bz-status-green"}`}>{row.status}</span> | {moment(row.bookingDate).format("MMM D")}</p>
                    </td>
                    <td className="bg-white whitespace-nowrap px-3 py-4 text-left text-sm font-bold">
                      {row?.eventName || row?.event_schedule_detail?.eventName || ""}
                      <p className="text-xs text-gray-400 font-normal text-wrap">{row.event_schedule_detail && row.event_schedule_detail.eventDate ? format(parseISO(row.event_schedule_detail.eventDate), "MMM d") : ""}</p>
                    </td>
                    <td className="bg-white whitespace-nowrap px-3 py-4 text-left text-sm font-bold">
                      {row.bookingQty ? row.bookingQty : "1"}
                      <p className="text-xs text-gray-400 font-normal text-wrap" title={row?.ticketsBought ? row.ticketsBought.map((ticket, i) => {
                        return ticket.bookingQty + " x " + ticket.ticketType + " "
                      }).join(" | ") : ""}>
                        {cutString(row?.ticketsBought ? row.ticketsBought.map((ticket, i) => {
                          return ticket.bookingQty + " x " + ticket.ticketType + " "
                        }).join(" | ") : "", 25, true)}
                      </p>
                    </td>
                    <td className="bg-white whitespace-nowrap px-3 py-4 text-left text-sm font-bold">
                      €{ticketAmount(row)}
                      <p className="text-xs text-gray-400 font-normal text-wrap">{(row.paymentStatus.toLowerCase() === "paid") ? "Paid Online" : row.paymentStatus}</p>
                    </td>
                    <td className="bg-white whitespace-nowrap px-3 py-4 text-center text-sm font-bold rounded-r-2xl">
                      <SessionBookingPopover
                        bookingDetails={row}
                        handleUpdate={() => {
                          getRecentBookings(recentBookingsPageNumber)
                          return dispatch(
                            dashboardActions.setUpdateSessionComp(
                              dashboardReduxState.updateSessionComp + 1
                            )
                          )
                        }}
                      />
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={6} className="bg-td-row hover:bg-td-row-h whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6 text-center rounded-2xl">
                      {/* There is no {bookingFilter !== "all" ? bookingFilter + " " : ""}bookings */}
                      {t("bookings.noBookingsYet")}
                    </td>
                  </tr>
                )}

              </tbody>
            </table>
          </div>)}
          {showViewMore ? (<div className={"flex flex-row justify-center my-4"}>
            <TwButton
              variant={"default-box"}
              className="sm:min-w-32 truncate !text-primary"
              onClick={() => {
                setRecentBookingsPageNumber(recentBookingsPageNumber + 1);
              }}
            >
              {t("inputFieldsTitle.viewMore")}
            </TwButton>
          </div>) : ""}
        </div>
      </div>
    </div>
  );
}
